import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Boxes, Save, Star, X } from 'lucide-react'
import { createProduct, getAccounts, getControlAccounts, getProductDiscount, getProducts, saveProductDiscount } from '../../api/master'
import { addPickLocation, deletePickLocation, listPickLocations, updatePickLocation } from '../../api/inventory'
import ErrorBanner from '../../components/ErrorBanner'
import { ScanButton } from '../../components/BarcodeScanner'
import Spinner from '../../components/Spinner'
import { HelpTip } from '../../components/HelpTip'
import { BackLink, DocHeader, StatusBadge } from '../../components/PageParts'
import OnboardingContinuePrompt from '../../components/onboarding/OnboardingContinuePrompt'
import OnboardingStepBar from '../../components/onboarding/OnboardingStepBar'
import { useToast } from '../../components/Toast.jsx'
import { useSession } from '../../session'
import { money } from '../sales/salesShared'
import { useBinChoice } from './ItemBulkActions'
import { Field, isHeading, isService, isTaxable, useMasterAccess, useOnboardingStep } from './masterShared'
import { useStandardDefaults } from '../accounting/accountingShared'
import { CreateOption } from '../../components/CreateOption'
import { returnTo } from '../../components/CreateOption'

const BLANK = {
  name: '', sku: '', barcode: '', description: '', unit_price: '', unit_cost: '',
  is_service: false, is_taxable: true, tax_rate: '', discount_type: 'none', discount_value: '', income_account_code: '',
  stock_kind: 'goods',
}
const KIND_LABEL = { goods: 'Bought to resell', material: 'Material', made: 'Made here' }
const KIND_HELP = 'Bought to resell: goods you sell as they are; their stock is Inventory. Material: a raw material you keep to make your products (fabric, thread, beads); its stock is Raw materials, and it shows under Inventory, Raw materials and as Materials on purchases. Made here: set under Production, What you make, with its recipe.'
const numOk = (v, max = Infinity) => String(v).trim() === '' || (Number.isFinite(Number(v)) && Number(v) >= 0 && Number(v) <= max)

// One item: what it's called, what it sells for and costs, tax, and its
// discount. Stock items also show where they're picked from and their stock.
export default function ItemForm() {
  const { itemId } = useParams()
  const isNew = !itemId
  const nav = useNavigate()
  // Opened from "+ Add a new …" in a dropdown: go back there afterwards.
  const { search } = useLocation()
  const done = returnTo(search, '/master/products')
  // "+ Add a new product" from a materials purchase starts it as a material.
  const startKind = new URLSearchParams(search).get('stock_kind') === 'material' ? 'material' : 'goods'
  const qc = useQueryClient()
  const { notify } = useToast()
  const { binTracking, has } = useSession()
  const production = has('production')
  const can = useMasterAccess()
  const onboarding = useOnboardingStep('products')
  const [item, setItem] = useState(null)
  // Revenue accounts an item's sales can go to (admins only).
  const { data: accounts = [] } = useQuery({ queryKey: ['accounts'], queryFn: getAccounts, enabled: can.accounts })
  const { data: controls = [] } = useQuery({ queryKey: ['control-accounts'], queryFn: getControlAccounts, enabled: can.accounts })
  const incomeAccounts = useMemo(() => accounts.filter((a) => a.type === 'Revenue' && !isHeading(a, accounts) && !/^\d{2}00$/.test(String(a.account_code))), [accounts])
  const [form, setForm] = useState(BLANK)
  const [savedDiscount, setSavedDiscount] = useState({ type: 'none', value: 0 })
  const [errors, setErrors] = useState({})
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(!isNew)

  useEffect(() => {
    if (isNew) { setForm({ ...BLANK, stock_kind: startKind }); setItem(null); return }
    let cancelled = false
    setLoading(true)
    Promise.all([getProducts(), getProductDiscount(itemId).catch(() => ({}))])
      .then(([rows, disc]) => {
        if (cancelled) return
        const found = (rows || []).find((r) => String(r.product_id) === String(itemId))
        if (!found) { setError('This product or service wasn’t found.'); return }
        const dType = disc?.discount_type || 'none'
        setItem(found)
        setSavedDiscount({ type: dType, value: Number(disc?.discount_value || 0) })
        setForm({
          name: found.name || '', sku: found.sku || '', barcode: found.barcode || '', description: found.description || '',
          unit_price: found.unit_price != null ? String(Number(found.unit_price)) : '',
          unit_cost: found.unit_cost != null ? String(Number(found.unit_cost)) : '',
          is_service: isService(found), is_taxable: isTaxable(found), stock_kind: found.stock_kind || 'goods',
          tax_rate: found.tax_rate != null && String(found.tax_rate) !== '' ? String(Number(found.tax_rate)) : '',
          discount_type: dType, discount_value: dType === 'none' ? '' : String(Number(disc?.discount_value || 0)),
          income_account_code: found.income_account_code || '',
        })
      })
      .catch((e) => !cancelled && setError(e.message || 'Couldn’t load this product or service.'))
      .finally(() => !cancelled && setLoading(false))
    return () => { cancelled = true }
  }, [itemId, isNew])

  const set = (patch) => setForm((f) => ({ ...f, ...patch }))
  const std = useStandardDefaults()
  const controlCode = (name) => String(controls.find((c) => c.name === name)?.account_code || '').trim()
  // POS sales and invoices each have an income account of their own (Transaction accounts).
  const baseIncome = (form.is_service && controlCode('ServiceRevenue')) || controlCode('Sales') || std.Sales || ''
  const tillIncome = controlCode(form.is_service ? 'TillServiceRevenue' : 'TillSales') || baseIncome
  const invoiceIncome = controlCode(form.is_service ? 'InvoiceServiceRevenue' : 'InvoiceSales') || baseIncome
  const defaultIncome = tillIncome === invoiceIncome ? tillIncome : `POS ${tillIncome}, invoices ${invoiceIncome}`
  const onHand = Number(item?.qty_on_hand || 0)

  const validate = () => {
    const e = {}
    if (!form.name.trim()) e.name = 'Give it a name.'
    if (!form.sku.trim()) e.sku = 'Give it a SKU (your code for it).'
    if (!numOk(form.unit_price)) e.unit_price = 'Enter a price of 0 or more.'
    if (!numOk(form.unit_cost)) e.unit_cost = 'Enter a cost of 0 or more.'
    if (form.is_taxable && !numOk(form.tax_rate, 20)) e.tax_rate = 'Between 0 and 20%, or blank.'
    if (form.discount_type !== 'none' && !(String(form.discount_value).trim() !== '' && numOk(form.discount_value, form.discount_type === 'percent' ? 100 : Infinity))) {
      e.discount_value = form.discount_type === 'percent' ? 'Between 0 and 100%.' : 'An amount of 0 or more.'
    }
    if (form.is_service && !isNew && !isService(item) && onHand !== 0) e.is_service = `It has ${onHand} in stock. Bring its stock to zero first.`
    return e
  }

  const save = async () => {
    const e = validate()
    setErrors(e)
    if (Object.keys(e).length) { setError('Check the highlighted fields.'); return }
    setSaving(true)
    setError('')
    try {
      const res = await createProduct({
        product_id: isNew ? undefined : Number(itemId),
        sku: form.sku.trim(), barcode: form.barcode.trim(), name: form.name.trim(), description: form.description.trim(),
        unit_cost: form.unit_cost, unit_price: form.unit_price,
        is_taxable: form.is_taxable, tax_rate: form.is_taxable && String(form.tax_rate).trim() ? Number(form.tax_rate) : null,
        is_service: form.is_service,
        ...(isNew && production && !form.is_service ? { stock_kind: form.stock_kind } : {}),
        // Only admins see (and so send) the income account.
        ...(can.accounts ? { income_account_code: form.income_account_code } : {}),
      })
      const id = res?.product_id || itemId
      const dValue = form.discount_type === 'none' ? 0 : Number(form.discount_value)
      if (form.discount_type !== savedDiscount.type || dValue !== savedDiscount.value) {
        await saveProductDiscount({ product_id: id, discount_type: form.discount_type, discount_value: dValue })
      }
      await qc.invalidateQueries({ queryKey: ['products'] })
      notify(`${form.name.trim()} ${isNew ? 'added' : 'saved'}`)
      if (onboarding.afterSave()) { if (isNew) { setForm(BLANK); setErrors({}) } return }
      nav(done)
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Couldn’t save this product or service.')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div className="ci-form-page"><Spinner label="Loading…" /></div>

  return (
    <div className="ci-form-page">
      <OnboardingStepBar step="products" />
      <DocHeader
        back={<BackLink to={done} label={done === '/master/products' ? 'Products & services' : 'Back'} />}
        title={isNew ? 'New product or service' : (item?.name || (isService(item) ? 'Service' : 'Product'))}
        badge={!isNew && (isService(item) ? <StatusBadge label="Service" tone="info" /> : <StatusBadge label="Product" tone="muted" />)}
        meta={isNew ? 'A product you stock, or a service you sell.' : [item?.sku, item?.barcode].filter(Boolean).join(' · ')}
        actions={!isNew && !isService(item) && can.tracked && (can.stock
          ? <Link className="ci-btn ci-btn-ghost" to={`/inventory/stock/${itemId}`}><Boxes size={15} />{onHand.toLocaleString()} on hand</Link>
          : <span className="mf-onhand"><Boxes size={15} />{onHand.toLocaleString()} on hand</span>)}
      />

      {error && <ErrorBanner message={error} />}

      <div className="ci-form-meta-card mf-form">
        <div className="ci-form-meta-top two">
          <Field label="Name" error={errors.name}>
            <input value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. Ankara fabric, 6 yards" />
          </Field>
          <Field label="SKU" error={errors.sku} hint="Your own code for it.">
            <input value={form.sku} onChange={(e) => set({ sku: e.target.value })} placeholder="e.g. P001" />
          </Field>
        </div>
        <div className="ci-form-meta-top two">
          <Field label="Barcode (optional)">
            <div className="scan-field">
              <input value={form.barcode} onChange={(e) => set({ barcode: e.target.value })} placeholder="Scan or type it" aria-label="Barcode (optional)" />
              <ScanButton onScan={(code) => set({ barcode: code })} />
            </div>
          </Field>
          <Field label="Description (optional)">
            <input value={form.description} onChange={(e) => set({ description: e.target.value })} placeholder="Shown on invoices and quotes" />
          </Field>
        </div>
        <div className="mf-checks">
          <label className="ci-form-toggle mf-check">
            <input type="checkbox" checked={form.is_service} onChange={(e) => set({ is_service: e.target.checked })} />
            Service, not kept in stock
          </label>
          <HelpTip text="For labour, delivery, repairs and other work you sell. Services never come off stock, and their sales go to Service Revenue instead of product sales." />
          {errors.is_service && <span className="acc-field-error">{errors.is_service}</span>}
        </div>
        {production && !form.is_service && (isNew ? (
          <div className="mf-kind">
            <span className="label-with-help">What is it? <HelpTip text={KIND_HELP} /></span>
            <div className="segmented" role="radiogroup" aria-label="What is it?">
              {['goods', 'material'].map((k) => (
                <button key={k} type="button" role="radio" aria-checked={form.stock_kind === k} className={form.stock_kind === k ? 'active' : ''} onClick={() => set({ stock_kind: k })}>
                  {KIND_LABEL[k]}<small>{k === 'goods' ? 'Sold as it is' : 'Used to make your products'}</small>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="mf-kind">
            <p className="doc-hint">What it is: {KIND_LABEL[form.stock_kind] || KIND_LABEL.goods} <HelpTip text={KIND_HELP} /> · Change it under <Link to="/inventory/production/products">Production, What you make</Link>, which moves its stock’s value too.</p>
          </div>
        ))}
      </div>

      <div className="ci-form-meta-card mf-form">
        <div className="mf-card-title">Price and tax</div>
        <div className="mf-checks">
          <label className="ci-form-toggle mf-check">
            <input type="checkbox" checked={form.is_taxable} onChange={(e) => set({ is_taxable: e.target.checked })} />
            Taxable
          </label>
        </div>
        <div className="ci-form-meta-top three">
          <Field label="Selling price" error={errors.unit_price}>
            <input value={form.unit_price} onChange={(e) => set({ unit_price: e.target.value })} inputMode="decimal" placeholder="0.00" />
          </Field>
          <Field label="Cost" error={errors.unit_cost} hint={form.is_service ? 'What it usually costs you to provide.' : (isNew ? 'What one usually costs you. Goods received set the actual cost.' : `Average cost now: ${money(item?.layer_avg_cost ?? item?.avg_cost ?? 0)}`)}>
            <input value={form.unit_cost} onChange={(e) => set({ unit_cost: e.target.value })} inputMode="decimal" placeholder="0.00" />
          </Field>
          <Field label="Tax rate (optional)" error={errors.tax_rate} hint={form.is_taxable ? 'Blank uses the location’s rate.' : 'Not taxed.'}>
            <input value={form.is_taxable ? form.tax_rate : ''} disabled={!form.is_taxable} onChange={(e) => set({ tax_rate: e.target.value })} inputMode="decimal" placeholder="e.g. 7.5" />
          </Field>
        </div>
        <div className="ci-form-meta-top three">
          <Field label="Discount">
            <select value={form.discount_type} onChange={(e) => set({ discount_type: e.target.value })}>
              <option value="none">No discount</option>
              <option value="percent">Percent off</option>
              <option value="amount">Amount off</option>
            </select>
          </Field>
          {form.discount_type !== 'none' && (
            <Field label={form.discount_type === 'percent' ? 'Percent off' : 'Amount off'} error={errors.discount_value} hint="Applied when it’s sold in store.">
              <input value={form.discount_value} onChange={(e) => set({ discount_value: e.target.value })} inputMode="decimal" placeholder={form.discount_type === 'percent' ? '10' : '0.00'} />
            </Field>
          )}
          {can.accounts && (
            <Field label={<>Income account <HelpTip text="Where its sales are recorded, in store and on invoices. Leave it on Default to use the products (or services) sold account for POS sales or invoices, set under Transaction accounts." /></>}>
              <select value={form.income_account_code} onChange={(e) => set({ income_account_code: e.target.value })}>
                <option value="">Default{defaultIncome ? ` (${defaultIncome})` : ''}</option>
                {form.income_account_code && !incomeAccounts.some((a) => a.account_code === form.income_account_code) && <option value={form.income_account_code}>{form.income_account_code}</option>}
                {incomeAccounts.map((a) => <option key={a.account_code} value={a.account_code}>{a.account_code} · {a.account_name}</option>)}
              </select>
            </Field>
          )}
        </div>
      </div>

      {!isNew && binTracking && can.stock && !isService(item) && <PickBins itemId={Number(itemId)} />}

      <div className="ci-form-action-bar">
        <div className="ci-form-action-left">
          <button type="button" className="ci-btn ci-btn-primary" disabled={saving} onClick={save}><Save size={14} />{saving ? 'Saving…' : (isNew ? 'Add' : 'Save')}</button>
          <button type="button" className="ci-btn ci-btn-ghost" onClick={() => nav(done)}>Cancel</button>
        </div>
      </div>

      <OnboardingContinuePrompt open={onboarding.open} saved="Saved" another="Add another product or service" next={onboarding.after} onAddAnother={onboarding.addAnother} onNext={onboarding.next} />
    </div>
  )
}

// Where the item is picked from when it's sold: one or more bins, one of
// them the main one. Changes save straight away.
function PickBins({ itemId }) {
  const { locations, locationId, setLocationId, bins } = useBinChoice()
  const [picks, setPicks] = useState(null)
  const [areaId, setAreaId] = useState('')
  const [error, setError] = useState('')
  const load = () => listPickLocations([itemId]).then((rows) => setPicks(rows || [])).catch(() => setPicks([]))
  useEffect(() => { load() }, [itemId]) // eslint-disable-line react-hooks/exhaustive-deps
  const run = async (fn) => {
    setError('')
    try { await fn(); await load() } catch (e) { setError(e?.response?.data?.error || e.message || 'Couldn’t change the pick bins.') }
  }
  const available = useMemo(() => bins.filter((b) => !(picks || []).some((p) => Number(p.area_id) === Number(b.area_id))), [bins, picks])
  return (
    <div className="ci-form-meta-card mf-form">
      <div className="mf-card-title">Pick bins <HelpTip text="Where staff pick this product from when it’s sold. The main bin is used first; add others for overflow." /></div>
      {error && <ErrorBanner message={error} />}
      {picks === null ? <Spinner label="Loading bins…" /> : picks.length === 0 ? (
        <p className="sum-muted">No pick bin yet.</p>
      ) : (
        <ul className="mf-pick-list">
          {picks.map((p) => (
            <li key={p.area_id}>
              <span>{p.path || `Bin #${p.area_id}`}</span>
              {Number(p.is_primary) === 1
                ? <StatusBadge label="Main bin" tone="good" />
                : <button type="button" className="acc-link-btn" onClick={() => run(() => updatePickLocation({ product_id: itemId, area_id: p.area_id, is_primary: true }))}><Star size={12} /> Make main</button>}
              <button type="button" className="mf-icon-btn" aria-label={`Remove ${p.path || 'bin'}`} onClick={() => run(() => deletePickLocation({ product_id: itemId, area_id: p.area_id }))}><X size={14} /></button>
            </li>
          ))}
        </ul>
      )}
      <div className="ci-form-meta-top three mf-pick-add">
        <Field label="Location">
          <select value={locationId} onChange={(e) => { setLocationId(e.target.value); setAreaId('') }}>
            {locations.map((l) => <option key={l.location_id} value={l.location_id}>{l.name}</option>)}
            <CreateOption kind="location" />
          </select>
        </Field>
        <Field label="Bin">
          <select value={areaId} onChange={(e) => setAreaId(e.target.value)}>
            <option value="">{available.length ? 'Choose a bin…' : 'No more bins here'}</option>
            {available.map((b) => <option key={b.area_id} value={b.area_id}>{b.path || b.name || b.area_id}</option>)}
          </select>
        </Field>
        <div className="mf-pick-btn">
          <button type="button" className="ci-btn ci-btn-ghost" disabled={!areaId}
            onClick={() => run(async () => { await addPickLocation({ product_id: itemId, area_id: Number(areaId), is_primary: !(picks || []).length }); setAreaId('') })}>
            Add bin
          </button>
        </div>
      </div>
    </div>
  )
}
